export enum McpAuthType {
  NONE = 'NONE',
  API_KEY = 'API_KEY',
  BEARER_TOKEN = 'BEARER_TOKEN',
  OAUTH2 = 'OAUTH2',
}

// HTTP is the original streamable-HTTP MCP transport (serverUrl + bearer/api-key/oauth2).
// RELAY carries the same JSON-RPC calls over the device's outbound WebSocket connection
// instead — no inbound tunnel/serverUrl required. See localDeviceRelay.ts for the envelope.
export enum McpConnectionTransport {
  HTTP = 'HTTP',
  RELAY = 'RELAY',
}

export enum McpConnectionStatus {
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR',
}

/** The two MCP-protocol tool-annotation hints the executor actually consults — a server's
 *  own, unverified claim about a tool's effect, consultative only. */
export interface McpToolAnnotationHints {
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
}

export const MCP_STRUCTURED_OUTPUT_KEY = 'structured';

export interface McpDiscoveredTool {
  name: string;
  description?: string;
  inputSchema: Record<string, unknown>;
  outputPropertyNames?: string[];
  outputArrayPropertyNames?: string[];
  /** Transient discovery data, stripped before persistence — only the flat
   *  readOnlyToolNames/destructiveToolNames name arrays on McpConnection below are ever
   *  stored, because of the DynamoDB nesting limit a per-tool annotations map would hit on
   *  a connection with many discovered tools. */
  annotations?: McpToolAnnotationHints;
}

export interface McpServerInfo {
  name: string;
  version: string;
  protocolVersion: string;
}

export interface McpOauthData {
  clientId: string;
  tokenEndpoint: string;
  authorizationEndpoint: string;
  registrationEndpoint?: string;
  scope?: string;
  refreshToken?: string; // encrypted at rest (same KMS key/context as credentials)
  tokenExpiresAt?: number; // epoch ms of the current access token
  clientSecret?: string; // encrypted at rest; present only for pre-registered providers (no DCR)
  pendingState?: string; // transient: random state for CSRF
  pendingCodeVerifier?: string; // transient: PKCE verifier, encrypted
  pendingExpiresAt?: number; // transient: TTL epoch ms (10 min)
}

export interface McpConnection {
  userId: string;
  connectionId: string;
  name: string;
  // Required for transport HTTP; unused (empty string) for transport RELAY.
  serverUrl: string;
  authType: McpAuthType;
  credentials?: string;
  status: McpConnectionStatus;
  serverInfo?: McpServerInfo;
  discoveredTools: McpDiscoveredTool[];
  enabledTools: string[];
  // Tool names only — structural and readable, never credentials or content.
  disabledTools?: string[];
  // Flattened out of each tool's McpDiscoveredTool.annotations at sync time — see the
  // comment there for why only these flat name arrays persist.
  readOnlyToolNames?: string[];
  destructiveToolNames?: string[];
  oauth?: McpOauthData;
  lastSyncAt?: number;
  createdAt: number;
  updatedAt: number;
  // Defaults to HTTP for existing rows written before transport existed.
  transport?: McpConnectionTransport;
  // Set only when transport is RELAY — the LocalDevices row this connection proxies through.
  deviceId?: string;
}

export interface CreateMcpConnectionInput {
  name: string;
  // Required for transport HTTP; omit (or pass '') for transport RELAY.
  serverUrl: string;
  authType: McpAuthType;
  credentials?: string;
  transport?: McpConnectionTransport;
  deviceId?: string;
}

export interface UpdateMcpConnectionInput {
  connectionId: string;
  name?: string;
  serverUrl?: string;
  authType?: McpAuthType;
  credentials?: string;
  enabledTools?: string[];
}

// Mirrors apps/scripts/localClaudeMcp JobStatus string values (queued/running/done/failed/
// paused/canceled) plus two terminal states the polling GraphQL query adds for a dead/
// unrecognized server (unreachable/unknown).
export enum DelegatedCodingTaskPollStatus {
  QUEUED = 'queued',
  RUNNING = 'running',
  DONE = 'done',
  FAILED = 'failed',
  PAUSED = 'paused',
  CANCELED = 'canceled',
  UNREACHABLE = 'unreachable',
  UNKNOWN = 'unknown',
}

export interface DelegatedCodingTaskStatusResult {
  status: DelegatedCodingTaskPollStatus;
  title?: string;
  prUrl?: string;
  logTail?: string;
  error?: string;
  /** Present on `done` results for a plan/technical-analysis job (plan_coding_task). */
  plan?: string;
  /** The agent's final prose (footer stripped, max 4000 chars); absent from old runners. */
  agentMessage?: string;
  /** ISO time the runner finished this attempt; absent from old runners. */
  finishedAt?: string;
  /** Present while a terminal session holds this job (daemon-side lock). */
  terminalSessionId?: string;
}

/**
 * What a daemon v2 negotiates on connect for a LOCAL_MCP_CODING connection — never
 * persisted, read once per dispatch to decide idempotency keying, PR-verification capability
 * and which repo to target. The local path never leaves the machine; only the origin's slug
 * does.
 */
export interface CodingTaskServerCapabilities {
  protocolVersion: string;
  idempotencyKey: boolean;
  checkPullRequest: boolean;
  // check_pull_request also returns checks / reviewDecision / reviewCommentCount.
  pullRequestSignals: boolean;
  // Whether this server gates the verification command for an externally-originated
  // delegation off by default — see DelegationSourceOrigin (agentDelegation.ts).
  sourceOriginGate: boolean;
  repositories: { slug: string; defaultBranch: string }[];
  ghAvailable: boolean;
  // Daemon can adopt a finished run into a terminal session (continue_coding_task_in_terminal).
  continueInTerminal?: boolean;
}
